import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon,
  UsersIcon,
  TruckIcon,
  TicketIcon,
  CreditCardIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  BuildingLibraryIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  BanknotesIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Biletat', href: '/tickets', icon: TicketIcon },
  { name: 'Klientët', href: '/clients', icon: UsersIcon },
  { name: 'Furnitorët', href: '/suppliers', icon: TruckIcon },
  { name: 'Pagesat', href: '/payments', icon: CreditCardIcon },
  { name: 'Shpenzimet', href: '/expenses', icon: BanknotesIcon },
  { name: 'Raporte', href: '/reports', icon: ChartBarIcon },
];

const settingsNav = [
  { name: 'Destinacionet', href: '/destinations', icon: MapPinIcon },
  { name: 'Kompanite Ajrore', href: '/airlines', icon: PaperAirplaneIcon },
  { name: 'Llogaritë Bankare', href: '/bank-accounts', icon: BuildingLibraryIcon },
  { name: 'Përdoruesit', href: '/users', icon: UserGroupIcon },
];

const debtsNav = [
  { name: 'Borxhe Klientësh', href: '/client-debts', icon: ExclamationTriangleIcon },
  { name: 'Borxhe Furnitorësh', href: '/supplier-debts', icon: ExclamationTriangleIcon },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavLink = ({ item, onClick }) => (
    <Link
      to={item.href}
      onClick={onClick}
      className={`flex items-center px-3 py-2 rounded-lg transition-colors ${
        location.pathname === item.href
          ? 'bg-primary-50 text-primary-700 font-medium'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <item.icon className="w-5 h-5 mr-3" />
      {item.name}
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl overflow-y-auto">
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <span className="text-xl font-bold text-primary-600">✈️ TravelPro</span>
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <nav className="p-4 space-y-1">
            {navigation.map((item) => (
              <NavLink key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
            ))}
            <div className="pt-4 mt-4 border-t">
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase">Borxhet</p>
              {debtsNav.map((item) => (
                <NavLink key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
              ))}
            </div>
            <div className="pt-4 mt-4 border-t">
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase">Konfigurime</p>
              {settingsNav.map((item) => (
                <NavLink key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r overflow-y-auto">
          <div className="flex items-center h-16 px-6 border-b">
            <span className="text-xl font-bold text-primary-600">✈️ TravelPro</span>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => (
              <NavLink key={item.name} item={item} />
            ))}
            <div className="pt-4 mt-4 border-t">
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase mb-2">Borxhet</p>
              {debtsNav.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </div>
            <div className="pt-4 mt-4 border-t">
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase mb-2">Konfigurime</p>
              {settingsNav.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </div>
          </nav>
          <div className="p-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{user?.name}</p>
                <p className="text-sm text-gray-500 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex items-center h-16 px-4 bg-white border-b lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
        </header>
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
